import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { PlainInput } from 'components/PlainInput'
import { SaveChanges } from 'components/SaveChanges'
import { InputType, SettingKey } from 'config'
import { IExtensionCost } from 'pages/LoanStructure/interfaces'
import { useEffect, useMemo, useState } from 'react'
import { setAdminConfig } from 'services'
import { confirm, ConvertInputValue, InputConvert, InputValidate, removeComma } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { defaultLockExtensionInputs, defaultProductInputs, defaultProgramInputs } from './constants'
import { ILoanGlobalSettings, IManualProgram } from './interface'

interface IProps {
  globalSetting: ILoanGlobalSettings | undefined
  setAction: (value: string) => void
  setGlobalSetting: (value: ILoanGlobalSettings) => void
}

export default function ManualPricingConfig(props: IProps) {
  const { globalSetting, setGlobalSetting, setAction } = props

  const [productInputs, setProductInputs] = useState<Record<string, InputType>>()
  const [inputs, setInputs] = useState<Record<string, InputType>[]>([])
  const [lockExtensionInputs, setLockExtensionInputs] = useState<Record<string, InputType>>()
  const [extensions, setExtensions] = useState<IExtensionCost>()
  const [isChanged, setChanged] = useState(false)

  useEffect(() => {
    if (!!globalSetting) {
      const newProductInputs = cloneDeep(defaultProductInputs())
      const newInputs: Record<string, InputType>[] = []
      const newLockExtensionInputs = cloneDeep(defaultLockExtensionInputs())

      Object.keys(newProductInputs).forEach((key) => {
        if ((globalSetting.manualPricingSetting as any)[key] !== undefined)
          newProductInputs[key].value = (globalSetting.manualPricingSetting as any)[key]
      })
      globalSetting.manualPricingSetting.programs.forEach((item) => {
        const inputItem = cloneDeep(defaultProgramInputs())
        Object.keys(inputItem).forEach((key) => {
          if ((item as any)[key] !== undefined) inputItem[key].value = (item as any)[key]
        })
        newInputs.push(inputItem)
      })
      Object.keys(newLockExtensionInputs).forEach(
        (key) => (newLockExtensionInputs[key].value = (globalSetting.lockExtensionCost as any)[key]),
      )
      setExtensions(globalSetting.lockExtensionCost)
      setProductInputs(newProductInputs)
      setInputs(newInputs)
      setLockExtensionInputs(newLockExtensionInputs)
    }

    setChanged(false)
  }, [globalSetting])

  const onAdd = () => {
    const newInputs = cloneDeep(inputs)
    const newProgram = cloneDeep(defaultProgramInputs())
    const defaultProrgam: IManualProgram = {
      id: Date.now(),
      programName: `New Program ${inputs.length + 1}`,
      rate: 10,
      price: 100,
      rateSpread: 0,
      exitFee: 0,
    }
    Object.keys(defaultProrgam).forEach((key) => {
      if ((defaultProrgam as any)[key] !== undefined) newProgram[key].value = (defaultProrgam as any)[key]
    })
    newInputs.push(newProgram)
    setInputs(newInputs)
    setChanged(true)
  }

  const onAddExtension = () => {
    if (!extensions) return
    const newData = cloneDeep(extensions)
    newData.extensions.push({
      extension: !!newData.extensions.length ? newData.extensions[newData.extensions.length - 1].extension + 1 : 1,
      priceAdjustment: !!newData.extensions.length
        ? newData.extensions[newData.extensions.length - 1].priceAdjustment
        : 0,
    })
    setExtensions(newData)
    setChanged(true)
  }

  const onChangeProduct = (key: string, value: string) => {
    if (!productInputs) return
    const newInputs = cloneDeep(productInputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setProductInputs(newInputs)
    setChanged(true)
  }

  const onChangeProgram = (index: number, key: string, value: string) => {
    const newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[index][key], value)
    newInputs[index][key].error = InputValidate({ ...newInputs[index][key], value })
    newInputs[index][key].value = value
    setInputs(newInputs)
    setChanged(true)
  }

  const onChangeLockExtensionLimit = (key: string, value: string) => {
    if (!lockExtensionInputs) return
    const newInputs = cloneDeep(lockExtensionInputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setLockExtensionInputs(newInputs)
    setChanged(true)
  }

  const onChangeExtension = (index: number, key: 'extension' | 'priceAdjustment', value: any) => {
    if (!extensions) return
    const newData = cloneDeep(extensions)
    value = removeComma(value)
    if (newData.extensions[index][key] == value) return
    const sameValueIndex = newData.extensions.findIndex((item) => item.extension == value)
    if (sameValueIndex !== -1 && sameValueIndex !== index) return
    newData.extensions[index][key] = value
    setExtensions(newData)
    setChanged(true)
  }

  const onRemove = async (index: number) => {
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Do you want to delete this product?
        <br />
        <span className="text-gray-600 text-base">Program Name: {inputs[index].programName.value}</span>
      </div>
    )

    const result = await confirm(content)

    if (!result) return

    const newInputs = cloneDeep(inputs)
    newInputs.splice(index, 1)
    setInputs(newInputs)
    setChanged(true)
  }

  const onRemoveExtension = async (index: number) => {
    if (!extensions) return
    const content = (
      <div className="text-gray-400 mb-4 text-[18px]">
        Do you want to delete this extension?
        <br />
        <span className="text-gray-600 text-base">
          Extension Days: {extensions.extensions[index].extension}{' '}
          {[0, 1].includes(extensions.extensions[index].extension) ? 'Day' : 'Days'}
        </span>
      </div>
    )
    const result = await confirm(content)
    if (!result) return

    const newData = cloneDeep(extensions)
    newData.extensions.splice(index, 1)
    setExtensions(newData)
    setChanged(true)
  }

  const onSubmit = async () => {
    if (!globalSetting || !productInputs || !lockExtensionInputs || !extensions) return

    const newSetting = cloneDeep(globalSetting)
    const newProductInput = cloneDeep(productInputs)
    const newInputs = cloneDeep(inputs)
    const newLockExtensionInputs = cloneDeep(lockExtensionInputs)
    const newExtension = cloneDeep(extensions)

    const newData = cloneDeep(globalSetting.manualPricingSetting)
    newData.programs = []
    let hasError = false
    const programNames: string[] = []

    Object.keys(newProductInput).forEach((key) => {
      newProductInput[key].error = InputValidate(newProductInput[key])
      if (newProductInput[key].error) hasError = true
      ;(newData as any)[key] = ConvertInputValue(newProductInput[key], newProductInput[key].value)
    })
    newInputs.forEach((item) => {
      const data: any = {}
      for (const key in item) {
        item[key].error = InputValidate(item[key])
        if (key !== 'section') data[key] = ConvertInputValue(item[key], item[key].value)
        if (item[key].error) hasError = true
        if (key === 'programName') {
          if (programNames.includes(data.programName)) {
            item[key].error = 'Already exists'
            hasError = true
          } else programNames.push(data.programName)
        }
      }
      newData.programs.push(data)
    })
    Object.keys(newLockExtensionInputs).forEach((key) => {
      newLockExtensionInputs[key].error = InputValidate(newLockExtensionInputs[key])
      if (newLockExtensionInputs[key].error) hasError = true
      ;(newExtension as any)[key] = ConvertInputValue(newLockExtensionInputs[key], newLockExtensionInputs[key].value)
    })
    setProductInputs(newProductInput)
    setInputs(newInputs)
    setLockExtensionInputs(newLockExtensionInputs)
    if (hasError) return

    newSetting.manualPricingSetting = newData
    newSetting.lockExtensionCost = newExtension

    setAction('setting')
    await setAdminConfig(SettingKey.GLOBAL_SETTINGS, newSetting)
    setGlobalSetting(newSetting)
    setAction('')
  }

  const renderProduct = useMemo(() => {
    if (!productInputs) return <></>

    return (
      <div>
        <div className="relative mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.keys(productInputs).map((key, index) => {
            const input = productInputs[key]

            return (
              <div className={`input md:col-span-${input.span || 1} `} key={index}>
                <RenderInput input={input} Key={key} onChange={onChangeProduct} />
              </div>
            )
          })}
        </div>
      </div>
    )
  }, [productInputs])

  const renderProgram = useMemo(() => {
    if (!inputs.length) return <></>

    return (
      <div>
        {inputs.map((item, index) => (
          <div className="relative mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.keys(item).map((key, idx) => {
              const input = item[key]
              let additionalElements = null

              if (key === 'section' && inputs.length > 1) {
                input.title = `Program ${index + 1}`

                additionalElements = (
                  <div className="flex">
                    <span className="btn-icon !text-red-500" onClick={() => onRemove(index)}>
                      <TrashIcon className="w-4 h-4" />
                    </span>
                  </div>
                )
              } else if (key === 'id') return <></>

              return (
                <div className={`input md:col-span-${input.span || 1} `} key={`${index}-${idx}`}>
                  <RenderInput
                    input={{ ...input, additionalElements }}
                    Key={key}
                    onChange={(key: string, value: string) => onChangeProgram(index, key, value)}
                  />
                </div>
              )
            })}
          </div>
        ))}
      </div>
    )
  }, [inputs])

  const renderLockExtensionLimit = useMemo(() => {
    if (!lockExtensionInputs) return <></>

    return (
      <div>
        <div className="relative mb-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.keys(lockExtensionInputs).map((key, index) => {
            const input = lockExtensionInputs[key]

            return (
              <div className={`input md:col-span-${input.span || 1} `} key={index}>
                <RenderInput input={input} Key={key} onChange={onChangeLockExtensionLimit} />
              </div>
            )
          })}
        </div>
      </div>
    )
  }, [lockExtensionInputs])

  const renderExtensions = useMemo(() => {
    if (!extensions) return <></>

    return (
      <table className="table w-fit text-center text-sm">
        <thead>
          <tr className="bg-gray-100">
            <th className="border p-2 whitespace-nowrap w-1/2">Extension Days</th>
            <th className="border p-2 whitespace-nowrap w-1/2">Price Adjustment</th>
            <th className="border py-2 px-5">Action</th>
          </tr>
        </thead>
        <tbody>
          {extensions.extensions.map((item, index) => (
            <tr key={index}>
              <td className="border p-1">
                <div className="flex justify-center">
                  <PlainInput
                    value={item.extension}
                    origin={item.extension}
                    content={`${item.extension} ${[0, 1].includes(item.extension) ? 'Day' : 'Days'}`}
                    onChange={(value: any) => onChangeExtension(index, 'extension', value)}
                    className="w-full"
                  />
                </div>
              </td>
              <td className="border p-1">
                <div className="flex justify-center">
                  <PlainInput
                    value={item.priceAdjustment}
                    origin={item.priceAdjustment}
                    content={item.priceAdjustment}
                    onChange={(value: any) => onChangeExtension(index, 'priceAdjustment', value)}
                    className="w-full"
                  />
                </div>
              </td>
              <td className="p-1 border">
                <div className="flex justify-center">
                  <div
                    className="w-fit p-1 transition-all duration-200 hover:cursor-pointer hover-shadow1 rounded"
                    onClick={() => onRemoveExtension(index)}
                  >
                    <TrashIcon className="w-4 h-4 hover:cursor-pointer text-red-700" />
                  </div>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )
  }, [extensions])

  return (
    <div className="relative mt-2">
      <SaveChanges show={isChanged} label="Save Changes" onSave={onSubmit} />

      <div className="flex items-center justify-between border-b mb-2 px-2">
        <span className="text-base font-bold">Default Values</span>
        <div className="flex items-center gap-1 hover:cursor-pointer text-sm text-shade-blue" onClick={onAdd}>
          <PlusIcon className="w-4 h-4" />
          <span>Add Program</span>
        </div>
      </div>
      {renderProduct}
      {renderProgram}

      <div className="flex items-center justify-between border-b mb-2 px-2">
        <span className="text-base font-bold">Lock Extension Costs</span>
        <div className="flex items-center gap-1 hover:cursor-pointer text-sm text-shade-blue" onClick={onAddExtension}>
          <PlusIcon className="w-4 h-4" />
          <span>Add</span>
        </div>
      </div>
      {renderLockExtensionLimit}
      {renderExtensions}
    </div>
  )
}
